import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DISCREPANCY_SCOPE_LABELS,
  DISCREPANCY_STATUS_LABELS,
  hasAtLeastRole,
  lireAdresseEcart,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';
import { useTitrePage } from '../../hooks/useTitrePage';
import { BadgeCopack } from '../../components/BadgeCopack';
import { DrilldownEcart } from './DrilldownEcart';
import { FilDeVie } from './FilDeVie';
import { ValidationEcart } from './ValidationEcart';
import { classeMontant } from '../../components/montant';

const ONGLETS = [
  { cle: 'jours', libelle: 'Jour par jour' },
  { cle: 'instruction', libelle: 'Instruction' },
] as const;

type Onglet = (typeof ONGLETS)[number]['cle'];

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDate(iso: string | null): string {
  if (!iso) return '—';
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : iso;
}

/**
 * Fiche d'un écart, à URL stable et partageable (P4.1).
 *
 * L'adresse porte la **clé suivie** — code article, ou famille préfixée — et
 * non l'identifiant de l'écart : un écart soldé puis réapparu change de ligne,
 * et un lien envoyé par courriel cesserait d'ouvrir quoi que ce soit.
 *
 * Deux onglets, parce qu'un instructeur fait deux choses différentes : établir
 * *où* l'écart se creuse, puis l'expliquer. Les mélanger sur un même écran
 * oblige à faire défiler l'un pour atteindre l'autre.
 */
/**
 * Ce qu'un onglet doit montrer d'une fiche.
 *
 * Une famille porte un identifiant technique et un libelle qui enumere ses
 * membres : les mettre tels quels remplirait la barre d'onglets d'un UUID et de
 * cent caracteres de parentheses. On garde ce qui distingue — le code article,
 * ou le nom de la famille — et on coupe le reste.
 */
const LONGUEUR_TITRE = 42;

function titreDeLaFiche(ligne: { maille: string; cle: string; libelle: string }): string {
  const libelle =
    ligne.libelle.length > LONGUEUR_TITRE
      ? `${ligne.libelle.slice(0, LONGUEUR_TITRE).trimEnd()}…`
      : ligne.libelle;

  // L'identifiant d'une famille ne dit rien a personne : son nom, si.
  return ligne.maille === 'FAMILLE' ? `Famille ${libelle}` : `Écart ${ligne.cle} · ${libelle}`;
}

export function FicheEcart(): ReactElement {
  const { code = '' } = useParams<{ code: string }>();
  const { user } = useAuth();
  const peutValider = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;
  const [onglet, setOnglet] = useState<Onglet>('jours');
  /** Journée demandée depuis le fil de vie : le clic bascule d'onglet. */
  const [jourDemande, setJourDemande] = useState<string | null>(null);

  const adresse = lireAdresseEcart(code);

  const ecart = useQuery({
    queryKey: ['ecart', 'adresse', code],
    queryFn: () => api.ecartParAdresse(code),
    enabled: adresse !== null,
  });

  /**
   * Le titre de l'onglet gagne le **libelle** des que la fiche l'a.
   *
   * La route sait deja dire « Ecart 433444 » — c'est le minimum, et il s'affiche
   * tout de suite. Mais quand on a six onglets ouverts, « FRANGELICO 6/700 ML »
   * est ce qui permet de retrouver le bon sans les parcourir un par un.
   */
  useTitrePage(ecart.data ? titreDeLaFiche(ecart.data) : null);

  if (adresse === null) {
    return (
      <>
        <h1>Écart introuvable</h1>
        <p className="alerte alerte--erreur" role="alert">
          « {code} » n&apos;est pas une adresse d&apos;écart. Une adresse est un code article
          (par exemple <code>435173</code>) ou une famille (<code>famille-…</code>). Vérifiez le
          lien que vous avez reçu.
        </p>
      </>
    );
  }

  if (ecart.isPending) {
    return <p className="chargement">Ouverture de la fiche…</p>;
  }

  if (ecart.isError) {
    return (
      <>
        <h1>Écart {code}</h1>
        <p className="alerte alerte--erreur" role="alert">
          {messageErreur(ecart.error)}
        </p>
      </>
    );
  }

  const ligne = ecart.data;

  return (
    <>
      <h1>
        Écart {ligne.cle} <span className="titre-secondaire">{ligne.libelle}</span>
      </h1>

      <div className="carte">
        <dl className="fiche">
          <div>
            <dt>Maille</dt>
            <dd>{DISCREPANCY_SCOPE_LABELS[ligne.maille]}</dd>
          </div>
          <div>
            <dt>Statut</dt>
            <dd>
              <span
                className={
                  ligne.statut === 'ACTIF' ? 'etiquette etiquette--attente' : 'etiquette'
                }
              >
                {DISCREPANCY_STATUS_LABELS[ligne.statut]}
              </span>
            </dd>
          </div>
          <div>
            <dt>Copacking</dt>
            <dd>
              <BadgeCopack role={ligne.copack} />
            </dd>
          </div>
          <div>
            <dt>Apparu le</dt>
            <dd>{formaterDate(ligne.apparuLe)}</dd>
          </div>
          <div>
            <dt>Ancienneté</dt>
            <dd>{ligne.ancienneteJours} jour(s)</dd>
          </div>
          <div>
            <dt>Écart cumulé</dt>
            <dd className={classeMontant(ligne.ecartUvc)}>{formaterNombre(ligne.ecartUvc)} UVC</dd>
          </div>
          <div>
            <dt>Reste à expliquer</dt>
            <dd className={classeMontant(ligne.ecartResiduel)}>
              {formaterNombre(ligne.ecartResiduel)} UVC
            </dd>
          </div>
          {ligne.codpros.length > 1 && (
            <div>
              <dt>Codes agrégés</dt>
              <dd>{ligne.codpros.join(' / ')}</dd>
            </div>
          )}
        </dl>

        <div className="onglets" role="tablist" aria-label="Sections de la fiche">
          {ONGLETS.map((o) => (
            <button
              key={o.cle}
              type="button"
              role="tab"
              id={`onglet-${o.cle}`}
              aria-selected={onglet === o.cle}
              aria-controls={`panneau-${o.cle}`}
              className={`onglet${onglet === o.cle ? ' onglet--actif' : ''}`}
              onClick={() => setOnglet(o.cle)}
            >
              {o.libelle}
            </button>
          ))}
        </div>
      </div>

      <div
        role="tabpanel"
        id={`panneau-${onglet}`}
        aria-labelledby={`onglet-${onglet}`}
        tabIndex={-1}
      >
        {onglet === 'jours' ? (
          <DrilldownEcart
            // Remonter le drill-down est ce qui ouvre la journee demandee :
            // voir le commentaire d'etat initial dans le composant.
            key={jourDemande ?? 'aucune-journee-demandee'}
            id={ligne.id}
            jourOuvert={jourDemande}
          />
        ) : (
          <>
            <FilDeVie
              ecart={ligne}
              peutCommenter={peutValider}
              onJour={(jour) => {
                // Le fil renvoie vers la journée : c'est le geste naturel après
                // avoir lu « l'écart a bougé le 27/04 ».
                setJourDemande(jour);
                setOnglet('jours');
              }}
            />
            <ValidationEcart ecart={ligne} peutValider={peutValider} />
          </>
        )}
      </div>
    </>
  );
}
